import React, { useState, useEffect } from "react";
import { updateUserApi, getUserPublicKeyApi } from "../../../api/userApi.jsx";
import {
  importPublicKey,
  encryptData,
  bufferToBase64,
} from "../../../utils/cryptoUtils.js";

// UUID 생성을 위한 유틸리티
const generateUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const UserDetails = ({
  selectedUser,
  setSelectedUser,
  onSuccess,
  onCancel,
}) => {
  const [errorMessages, setErrorMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isPasswordChangeChecked, setIsPasswordChangeChecked] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 50);
  }, []);

  useEffect(() => {
    setErrorMessages({});
  }, [selectedUser]);

  useEffect(() => {
    if (selectedUser?.userUid) {
      setIsPasswordChangeChecked(false);
      setSelectedUser((prevDetails) => ({
        ...prevDetails,
        password: "",
        passwordConfirm: "",
      }));
    }
  }, [selectedUser?.userUid, setSelectedUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedUser((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
    setErrorMessages((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handlePasswordChangeChecked = (e) => {
    setIsPasswordChangeChecked(e.target.checked);
    if (!e.target.checked) {
      setSelectedUser((prevDetails) => ({
        ...prevDetails,
        password: "",
        passwordConfirm: "",
      }));
      setErrorMessages({});
    }
  };

  const validateForm = () => {
    let errors = {};

    // 이름 유효성 체크
    if (
      !selectedUser.name ||
      selectedUser.name.length < 2 ||
      selectedUser.name.length > 16
    ) {
      errors.name = "이름은 2자 이상 16자 이하로 입력하세요.";
    }

    // 연락처 유효성 체크
    const phoneRegex = /^(010|02|0\d{2})-(\d{3,4})-(\d{4})$/;
    if (!selectedUser.phone || !phoneRegex.test(selectedUser.phone)) {
      errors.phone = "유효한 연락처 형식이 아닙니다. 예: 010-1234-5678";
    }

    // 이메일 유효성 체크
    if (
      !selectedUser.email ||
      !selectedUser.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
    ) {
      errors.email = "유효한 이메일 주소를 입력하세요.";
    }

    // 비밀번호 유효성 체크
    if (isPasswordChangeChecked) {
      if (!selectedUser.password) {
        errors.password = "비밀번호를 입력하세요.";
      } else if (
        selectedUser.password.length < 8 ||
        selectedUser.password.length > 52
      ) {
        errors.password = "비밀번호는 8자 이상 52자 이하로 입력하세요.";
      }

      if (selectedUser.password !== selectedUser.passwordConfirm) {
        errors.passwordConfirm = "비밀번호가 일치하지 않습니다.";
      }
    }

    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setErrorMessages(errors);
      return;
    }

    setLoading(true);

    try {
      let updateData = {
        name: selectedUser.name,
        phone: selectedUser.phone,
        email: selectedUser.email,
      };

      if (isPasswordChangeChecked && selectedUser.password) {
        const keyId = generateUUID();
        const response = await getUserPublicKeyApi(keyId);

        if (response.success && response.data?.publicKey) {
          const publicKeyString = response.data.publicKey;
          const publicKey = await importPublicKey(publicKeyString);
          const encryptedPassword = await encryptData(
            publicKey,
            selectedUser.password
          );
          const base64EncryptedPassword = bufferToBase64(encryptedPassword);

          updateData = {
            ...updateData,
            keyId: keyId,
            password: base64EncryptedPassword,
          };
        } else {
          setErrorMessages({
            general: "공개키를 가져오는 중 오류가 발생했습니다.",
          });
          return;
        }
      }

      // API에서 signInId를 받아서 사용해야 하는데, selectedUser에서 가져오는 것은 userAuth 객체이므로 signInId를 추출
      const signInId = selectedUser.userAuth?.signInId || selectedUser.signInId;
      const response = await updateUserApi(signInId, updateData);

      if (response.success) {
        if (onSuccess) onSuccess();
      } else {
        setErrorMessages({ general: "정보 업데이트에 실패했습니다." });
      }
    } catch (error) {
      // console.error("API 호출 중 오류:", error);
      setErrorMessages({ general: "정보 업데이트 중 오류가 발생했습니다." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`bg-white shadow rounded-lg transform transition-transform duration-200 ease-out ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div className="flex justify-between items-center border-b px-4 pt-3 pb-2">
        <h2 className="text-base font-semibold">관리자 상세 정보</h2>
        <button
          onClick={onCancel}
          className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-black hover:bg-gray-200 focus:outline-none text-[10px] font-bold"
        >
          &#x2715;
        </button>
      </div>

      <div className="p-4">
        {errorMessages.general && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {errorMessages.general}
          </div>
        )}

        {/* 아이디 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            아이디
          </label>
          <input
            type="text"
            name="signInId"
            value={
              selectedUser?.userAuth?.signInId || selectedUser?.signInId || ""
            }
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed text-left"
          />
        </div>

        {/* 이름 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            이름 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={selectedUser?.name || ""}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errorMessages.name ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="ex) 홍길동"
          />
          {errorMessages.name && (
            <p className="text-red-500 text-xs mt-1">{errorMessages.name}</p>
          )}
        </div>

        {/* 연락처 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            연락처 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="phone"
            value={selectedUser?.phone || ""}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errorMessages.phone ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="ex) 010-1234-5678"
          />
          {errorMessages.phone && (
            <p className="text-red-500 text-xs mt-1">{errorMessages.phone}</p>
          )}
        </div>

        {/* 이메일 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            이메일 <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={selectedUser?.email || ""}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errorMessages.email ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="ex) user@example.com"
          />
          {errorMessages.email && (
            <p className="text-red-500 text-xs mt-1">{errorMessages.email}</p>
          )}
        </div>

        {/* 권한 표시 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            권한
          </label>
          <input
            type="text"
            value={
              selectedUser?.roles
                ?.map((role) =>
                  role === "SUPER" ? "슈퍼관리자" : "일반관리자"
                )
                .join(", ") || ""
            }
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
          />
        </div>

        {/* 비밀번호 변경 체크박스 */}
        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isPasswordChangeChecked}
              onChange={handlePasswordChangeChecked}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">
              비밀번호 변경
            </span>
          </label>
        </div>

        {/* 비밀번호 변경 필드들 */}
        {isPasswordChangeChecked && (
          <>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                새 비밀번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                name="password"
                value={selectedUser?.password || ""}
                autoComplete="off"
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errorMessages.password ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="새 비밀번호를 입력하세요."
              />
              {errorMessages.password && (
                <p className="text-red-500 text-xs mt-1">
                  {errorMessages.password}
                </p>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                비밀번호 확인 <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                name="passwordConfirm"
                value={selectedUser?.passwordConfirm || ""}
                autoComplete="off"
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errorMessages.passwordConfirm
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="비밀번호를 다시 입력하세요."
              />
              {errorMessages.passwordConfirm && (
                <p className="text-red-500 text-xs mt-1">
                  {errorMessages.passwordConfirm}
                </p>
              )}
            </div>
          </>
        )}
      </div>

      <div className="border-t flex justify-end space-x-2 py-3 px-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          disabled={loading}
        >
          취소
        </button>
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "수정 중..." : "수정"}
        </button>
      </div>
    </div>
  );
};

export default UserDetails;
